import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApiGet, fetchApiPost } from "./utils";

/**
 * API client for the new payment-remittance-PDF orchestration flow
 * (mono-bun backend, proxied through NX `/payment-remittance/...`).
 *
 * This is intentionally separate from the legacy
 * `useUploadBulkPaymentRemittanceJob` (zip-and-ship) hook in
 * `customer.ts` — different backend, different shape, different lifecycle.
 */

// ---------- Types --------------------------------------------------------

export interface PaymentRemittanceCreateFile {
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface PaymentRemittanceCreatePayload {
  customer_id: string;
  /** YYYY-MM-DD; must be the first day of the month. */
  period_date: string;
  files: PaymentRemittanceCreateFile[];
}

export interface PaymentRemittanceCreatedUpload {
  source_pdf_id: string;
  document_id: string;
  original_filename: string;
  sort_filename: string;
  upload_order_index: number;
  bucket: string;
  key: string;
  region: string;
  presigned_put_url: string;
}

export interface PaymentRemittanceCreateResult {
  batch_id: string;
  uploads: PaymentRemittanceCreatedUpload[];
}

export interface PaymentRemittanceFinalizeResult {
  batch_id: string;
  /** Stringified BigInt — grandparent job id in pg_job_queue. */
  process_job_id: string;
  source_pdf_ids: string[];
}

export interface PaymentRemittanceListSourcePdf {
  id: string;
  original_filename: string;
  upload_order_index: number;
  upload_status: string;
  processing_status: string;
  total_page_count: number | null;
  classified_page_count: number;
  failed_page_count: number;
}

export interface PaymentRemittanceListCustomer {
  long_name: string;
}

export interface PaymentRemittanceListRow {
  id: string;
  customer_id: string;
  Customer: PaymentRemittanceListCustomer;
  status: string;
  period_date: string;
  created_at: string;
  uploads_finalized_at: string | null;
  process_job_id: string | null;
  counts_total_pdfs: number;
  counts_terminal_pdfs: number;
  counts_failed_pdfs: number;
  counts_total_pages: number;
  counts_includable_pages: number;
  needs_manual_review: boolean;
  has_active_assembly_version: boolean;
  source_pdfs: PaymentRemittanceListSourcePdf[];
}

export interface PaymentRemittanceListResult {
  rows: PaymentRemittanceListRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PaymentRemittanceDownloadLinkSourcePdf {
  source_pdf_id: string;
  original_filename: string;
  upload_order_index: number;
  processing_status: string;
  bucket: string;
  key: string;
  region: string;
  presigned_get_url: string;
}

export interface PaymentRemittanceDownloadLinkFinal {
  assembly_version_id: string;
  version_number: number;
  status: string;
  filename: string;
  bucket: string;
  key: string;
  region: string;
  presigned_get_url: string;
}

export interface PaymentRemittanceDownloadLinksResult {
  batch_id: string;
  expires_in_seconds: number;
  source_pdfs: PaymentRemittanceDownloadLinkSourcePdf[];
  final_assembled: PaymentRemittanceDownloadLinkFinal | null;
}

// ---------- Hooks --------------------------------------------------------

const LIST_QUERY_KEY = "paymentRemittancePdfsList";
const DOWNLOAD_LINKS_QUERY_KEY = "paymentRemittancePdfsDownloadLinks";

interface ListOptions {
  customer_id?: string;
  enabled?: boolean;
}

export function useListPaymentRemittanceBatches(options?: ListOptions) {
  const queryParams = new URLSearchParams();
  if (options?.customer_id) {
    queryParams.set("customer_id", options.customer_id);
  }

  return useQuery({
    queryKey: [LIST_QUERY_KEY, queryParams.toString()],
    queryFn: () =>
      fetchApiGet<PaymentRemittanceListResult>(
        "payment-remittance/batches",
        queryParams,
      ),
    enabled: options?.enabled ?? true,
    retry: 1,
  });
}

export function useCreatePaymentRemittanceBatch() {
  const queryClient = useQueryClient();

  return async (payload: PaymentRemittanceCreatePayload) => {
    const result = await fetchApiPost<PaymentRemittanceCreateResult>(
      "payment-remittance/batches",
      payload,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    }
    return result;
  };
}

export function useFinalizePaymentRemittanceBatch() {
  const queryClient = useQueryClient();

  return async (batchId: string) => {
    const result = await fetchApiPost<PaymentRemittanceFinalizeResult>(
      `payment-remittance/batches/${encodeURIComponent(batchId)}/finalize`,
      {},
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    }
    return result;
  };
}

interface DownloadLinksOptions {
  batchId: string | null;
  enabled?: boolean;
}

export function useGetPaymentRemittanceDownloadLinks(options: DownloadLinksOptions) {
  return useQuery({
    queryKey: [DOWNLOAD_LINKS_QUERY_KEY, options.batchId],
    queryFn: () =>
      fetchApiGet<PaymentRemittanceDownloadLinksResult>(
        `payment-remittance/batches/${encodeURIComponent(options.batchId ?? "")}/download-links`,
      ),
    enabled: !!options.batchId && (options.enabled ?? true),
    refetchOnWindowFocus: false,
    // Presigned URLs are short-lived; never cache between dialog opens.
    staleTime: 0,
    cacheTime: 0,
    retry: 1,
  });
}
